import { useMutation } from '@apollo/client/react';
import { TeamSpot } from '@optools/components/team-spot';
import { Button } from '@optools/components/ui/button';
import { Separator } from '@optools/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@optools/components/ui/sheet';
import { UPDATE_PIPELINE_CONFIG } from '@optools/services/operator-gateway/pipeline-config/mutations';
import { GET_PIPELINE_CONFIGS } from '@optools/services/operator-gateway/pipeline-config/queries';
import {
  GetPipelineConfigsQuery,
  PipelineConfigInfo,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { omit } from 'es-toolkit';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { PipelineConfigForm, PipelineConfigFormValues } from './pipeline-config-form';

export interface PipelineConfigSheetProps {
  config: PipelineConfigInfo | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export function PipelineConfigSheet({ config, onOpenChange, open }: PipelineConfigSheetProps) {
  const filteredConfig = config ? omit(config, ['__typename', 'id', 'teamId']) : {};

  // Maintains current config reference when sheet is closing to avoid flash of content
  const [_config, _setConfig] = useState(config);

  useEffect(() => {
    if (open) _setConfig(config);
  }, [open, config, _setConfig]);

  const [editMode, setEditMode] = useState(false);

  const [updatePipelineConfigMutation, { loading }] = useMutation(UPDATE_PIPELINE_CONFIG, {
    onCompleted: ({ updatePipelineConfig }) => {
      if (updatePipelineConfig) {
        toast.success('Pipeline config successfully updated');
        setEditMode(false);
      } else {
        toast.error('Pipeline config failed to update', { description: 'Please try again.' });
      }
    },
    update: (cache, { data }) => {
      if (!data?.updatePipelineConfig) return;

      const query = cache.readQuery<GetPipelineConfigsQuery>({
        query: GET_PIPELINE_CONFIGS,
      });

      if (!query) return;

      const configIndex = query.pipelineConfigs.findIndex(
        (item) => item.id === data.updatePipelineConfig?.id,
      );

      if (configIndex === -1) return;

      const pipelineConfigs = [...query.pipelineConfigs];

      pipelineConfigs.splice(configIndex, 1, {
        ...config,
        ...data.updatePipelineConfig,
      });

      cache.writeQuery<GetPipelineConfigsQuery>({
        data: { pipelineConfigs },
        query: GET_PIPELINE_CONFIGS,
      });
    },
  });

  const handleSubmit = (values: PipelineConfigFormValues) => {
    updatePipelineConfigMutation({
      variables: { pipelineConfig: values, teamId: config?.teamId ?? 0 },
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (loading) return;
    setEditMode(false);
    onOpenChange(open);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <TeamSpot id={_config?.teamId} showAbbreviation={false} size={32} />
              <span>Team ID: {_config?.teamId}</span>
            </div>
            <span>Config ID: {_config?.id}</span>{' '}
          </SheetTitle>
          <SheetDescription className="sr-only">Edit the pipeline config</SheetDescription>
          <Separator className="mt-4" />
        </SheetHeader>

        <PipelineConfigForm
          config={filteredConfig}
          editMode={editMode}
          loading={loading}
          onCancel={() => setEditMode(false)}
          onSubmit={handleSubmit}
        />

        {!editMode && (
          <div className="mt-12 flex justify-end">
            <Button className="w-32" onClick={() => setEditMode(true)}>
              Edit
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
